import React from "react";
import moment, { Duration } from "moment/moment";
import { ProductType } from "./ProjectMarketplacePageWrapper";
import { certificate_projects as certificateProjectsAdmin } from "../../openapi-typescript/admin";
import { certificate_projects as certificateProjectsApproved } from "../../openapi-typescript/approved";
import { useAPIQuery } from "../../shared/customHoooks/useAPI";
import { keysToSnake, spacing } from "../../shared/global";
import { useLocalStorageUntilLogoutPerKey } from "../../shared/customHoooks/useLocalStorageUntilLogout";
import { PriceHistoryChartPanel, PriceHistoryProps } from "./PriceHistoryChart";
import { Col, Row } from "antd";
import { PriceHistoryChartFilter } from "./PriceHistoryChartFilter";
import { MethodType } from "../markets/dailyCurves/carbonComponent/CertificateCurve";
import { ProductSelectedType } from "./MarketSnapshot";
import { ContentWithChartProps } from "../dashboards/WidgetPanel";
import { COREFiltersProps } from "../../COREDesignSystem/Form/COREFilters";
import "./PriceHistory.less";
import classNames from "classnames";
import { AuLocation } from "./PriceHistoryLocationSelect";
import {
  carbonIntAllProducts,
  environmentalAllProducts,
  powerAllProducts,
} from "../dashboards/widgets/marketPrices/OHLCPriceHistoryWidget";

type productId = number;

export type Product = {
  shortName: string;
  name?: string;
  name_parts: string[];
  vintage: number;
  id: productId;
};

export type ZoomLevel = Duration;
export type ZoomLabel = string;

export const zoomButtons: Record<ZoomLabel, ZoomLevel> = {
  M: moment.duration(1, "months"),
  Q: moment.duration(3, "months"),
  H: moment.duration(6, "months"),
  "1Y": moment.duration(1, "years"),
  "3Y": moment.duration(3, "years"),
} as const;

export const checkIsCandleStickChart = (type: ProductType): boolean =>
  carbonIntAllProducts.includes(type) ||
  powerAllProducts.includes(type) ||
  environmentalAllProducts.includes(type);

export type SnapshotType = Exclude<
  certificateProjectsAdmin | certificateProjectsApproved,
  undefined
>;

export const useTradableProductsForType = (
  type?: ProductType,
  projectMethodType?: ProductSelectedType["projectMethodType"],
  location?: AuLocation["shortName"]
): {
  tradableProducts: Product[];
  loading: boolean;
  error: boolean;
  sync: boolean;
} => {
  let projectMethodTypeParams = {};
  if (projectMethodType) {
    projectMethodTypeParams = keysToSnake({
      tenorType: `is.null`,
      projectMethodType: `eq.${projectMethodType}`,
    });
  } else {
    projectMethodTypeParams = keysToSnake({
      projectMethodType: `is.null`,
    });
  }
  const isEnvironmentalProduct =
    type && environmentalAllProducts.includes(type) && type !== "lgc";
  const {
    sync,
    loading,
    error,
    data: { data: tradableProductsForProductType },
  } = useAPIQuery(
    "tradable_products",
    keysToSnake({
      ...(type && {
        productType: `eq.${type}`,
      }),
      ...(location && {
        "name_parts->>0": `eq.${location}`,
      }),
      isActive: `eq.true`,
      isStrip: `eq.false`,
      certificateProject: `is.null`,
      tradeType: `eq.firm`,
      order: `tenor_type.nullsfirst,delivery_year.nullsfirst,vintage.nullsfirst`,
      ...(isEnvironmentalProduct
        ? { or: `(tenor_type.is.null,tenor_type.eq.monthly)` }
        : { or: `(tenor_type.is.null,tenor_type.eq.yearly)` }),
      ...projectMethodTypeParams,
    })
  );
  return {
    sync: sync as boolean,
    loading: loading as boolean,
    error,
    tradableProducts: tradableProductsForProductType,
  };
};

export const getLabelFromProduct = (tp: Product): string => tp.shortName;
const defaultZoom: ZoomLabel = "1Y";

export type PriceHistoryChartFilterValues = Pick<
  PriceHistoryProps,
  "productType"
> & { zoomLevel: ZoomLabel } & Partial<{
    method: MethodType["certificate_project_type_name"];
    vintages?: number | number[];
    location?: AuLocation["shortName"];
  }>;

export const PriceHistoryChartWithFilter: React.FC<
  {
    empty: React.ReactElement;
    isWidget: boolean;
    localStorageKey?: string;
    productType?: PriceHistoryProps["productType"];
    widgetFilters?: PriceHistoryChartFilterValues;
    onWidgetFilter?: COREFiltersProps["onChange"];
  } & Partial<ContentWithChartProps>
> = ({
  localStorageKey,
  empty,
  productType = "accu",
  isWidget,
  setOnResetChart,
  setOnDownloadPNG,
  setProgressPercent,
  widgetFilters,
  onWidgetFilter,
}) => {
  const initialValue = {
    productType: productType,
    zoomLevel: defaultZoom,
    method: "generic",
    location: "NSW",
  };

  const [localStorageChart, setLocalStorageChart] =
    useLocalStorageUntilLogoutPerKey<PriceHistoryChartFilterValues>(
      "greenMarketSnapshotChart",
      {
        key: localStorageKey ?? "greenMarketSnapshotChart",
        initialValue,
      }
    );
  const isCandleStick = checkIsCandleStickChart(localStorageChart.productType);
  const isPowerProduct = powerAllProducts.includes(
    localStorageChart.productType.toLowerCase()
  );

  const locationParam = isPowerProduct
    ? widgetFilters?.location ?? localStorageChart.location
    : undefined;

  const { tradableProducts } = useTradableProductsForType(
    widgetFilters?.productType ?? localStorageChart.productType,
    widgetFilters && widgetFilters.method !== "generic"
      ? widgetFilters?.method
      : localStorageChart.method !== "generic"
      ? localStorageChart.method
      : null,
    locationParam
  );

  const defaultVintages =
    widgetFilters?.vintages ||
    localStorageChart.vintages ||
    (isCandleStick || isPowerProduct
      ? !isPowerProduct
        ? tradableProducts?.[0]?.id
        : [tradableProducts?.[0]?.vintage, tradableProducts?.[0]?.id]
      : tradableProducts?.map((p) => p.id));

  let products: Product[] = [];

  if (tradableProducts?.length) {
    const filterProducts = (
      vintages: PriceHistoryChartFilterValues["vintages"]
    ) =>
      tradableProducts.filter((p) =>
        Array.isArray(vintages) ? vintages.includes(p.id) : p.id === vintages
      );

    products = localStorageChart.vintages
      ? filterProducts(localStorageChart.vintages)
      : filterProducts(defaultVintages);
  }

  const productName =
    products.length > 0
      ? products[0].name
      : localStorageChart.productType.toUpperCase();
  return (
    <Row
      className={classNames([{ "price-history-chart-fit-parent": isWidget }])}
      gutter={isWidget ? [spacing.md, spacing.md] : [spacing.lg, spacing.lg]}
    >
      <Col
        className={classNames([
          { "price-history-chart-layout-filter": isWidget },
        ])}
        span={24}
      >
        <PriceHistoryChartFilter
          key={localStorageKey}
          isWidget={isWidget}
          value={{
            ...(widgetFilters ? widgetFilters : localStorageChart),
            vintages: defaultVintages,
          }}
          setValue={setLocalStorageChart}
          onWidgetFilter={onWidgetFilter}
        />
      </Col>
      <Col
        className={classNames([
          { "price-history-chart-layout-chart": isWidget },
        ])}
        span={24}
      >
        <PriceHistoryChartPanel
          key={localStorageKey}
          productName={productName}
          isWidget={isWidget}
          productType={localStorageChart.productType}
          zoomLevel={
            zoomButtons[widgetFilters?.zoomLevel ?? localStorageChart.zoomLevel]
          }
          products={products}
          empty={empty}
          setOnDownloadPNG={setOnDownloadPNG}
          setOnResetChart={setOnResetChart}
          setProgressPercent={setProgressPercent}
        />
      </Col>
    </Row>
  );
};
